import { useEffect, useRef } from 'react';
import useStore from '../../../store';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 20;

// Убираем вспомогательные функции calculateDistance, calculateCenter, calculateNewTransform,
// так как новая логика будет внутри handleWheel.

function usePcZoom(boardCanvasRef) {
  const { ref } = useStore.getState();
  const { puzzle } = ref;
  const { state } = puzzle;

  const isPanning = useRef(false);
  const startPanPos = useRef({ x: 0, y: 0 });
  const startTransform = useRef({ scale: 1, x: 0, y: 0 }); // Трансформация в начале пана
  const isSpacePressed = useRef(false);

  // Подписываемся на layout контейнера доски, он нужен для расчетов
  const boardContainerLayout = useStore(s => s.ref.puzzle.layout.boardContainer);

  useEffect(() => {
    if (!state.isInitialized) return;

    const boardCanvas = boardCanvasRef.current;
    if (!boardCanvas) return;

    const handleWheel = (event) => {
      // Реагируем только если зажат Ctrl
      if (!event.ctrlKey) {
        return;
      }
      event.preventDefault();

      // --- Получаем самые свежие данные из store ---
      const currentStore = useStore.getState();
      const { methods, boardTransform, layout, boardCanvas } = currentStore.ref.puzzle;
      const currentBoardContainerLayout = layout.boardContainer;

      // Проверка, что layout готов
      if (!currentBoardContainerLayout || typeof currentBoardContainerLayout.left !== 'number') {
          console.warn("Board container layout not ready for zoom calculation.");
          return;
      }

      // --- Координаты курсора ---
      const screenX = event.clientX;
      const screenY = event.clientY;

      // --- Координаты курсора относительно контейнера ---
      const cursorRelativeToContainerX = screenX - currentBoardContainerLayout.left;
      const cursorRelativeToContainerY = screenY - currentBoardContainerLayout.top;

      // --- Старая и новая шкала ---
      const oldScale = boardTransform.scale;
      const zoomFactor = event.deltaY < 0 ? 1.1 : 1 / 1.1;
      const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, oldScale * zoomFactor));

      // Если масштаб не изменился, выходим
      if (newScale === oldScale) return;

      // --- Старая трансляция ---
      const oldX = boardTransform.x + boardCanvas.layout.x;
      const oldY = boardTransform.y + boardCanvas.layout.y;

      // --- Расчет новой трансляции (альтернативная формула) ---
      const scaleRatio = newScale / oldScale;
      const newX = cursorRelativeToContainerX * (1 - scaleRatio) + oldX * scaleRatio - boardCanvas.layout.x;
      const newY = cursorRelativeToContainerY * (1 - scaleRatio) + oldY * scaleRatio - boardCanvas.layout.y;

      // --- Применение новой трансформации ---
      methods.setBoardTransform({ scale: newScale, x: newX, y: newY });
    };

    // --- Логика панорамирования (без изменений в расчетах, но с получением свежего state) ---
    const handlePointerDown = (event) => {
      if (
        event.target === boardCanvas &&
        (event.button === 2 || // Правая кнопка
         (event.button === 0 && event.ctrlKey) || // ЛКМ + Ctrl
         (event.button === 0 && isSpacePressed.current)) // ЛКМ + Пробел
      ) {
        event.preventDefault();
        // Получаем свежую трансформацию в момент начала панорамирования
        const currentStore = useStore.getState();
        const { boardTransform } = currentStore.ref.puzzle;

        isPanning.current = true;
        startPanPos.current = { x: event.clientX, y: event.clientY };
        startTransform.current = { ...boardTransform }; // Сохраняем transform на начало пана
        try {
          boardCanvas.setPointerCapture(event.pointerId);
          boardCanvas.style.cursor = 'grabbing';
        } catch (e) { console.warn('Failed to set pointer capture'); }
      }
    };

    const handlePointerMove = (event) => {
      if (!isPanning.current) return;

      // Опциональная проверка, если кнопка мыши отпущена (для надежности)
      // if (event.buttons === 0) { handlePointerUp(event); return; }

      const currentPos = { x: event.clientX, y: event.clientY };
      // Считаем смещение от НАЧАЛЬНОЙ точки пана
      const dx = currentPos.x - startPanPos.current.x;
      const dy = currentPos.y - startPanPos.current.y;

      // Применяем смещение к НАЧАЛЬНОЙ трансформации (которая была в момент handlePointerDown)
      const newX = startTransform.current.x + dx;
      const newY = startTransform.current.y + dy;

      // Получаем актуальный метод setBoardTransform
      const { setBoardTransform } = useStore.getState().ref.puzzle.methods;

      setBoardTransform({
        scale: startTransform.current.scale, // Масштаб при пане не меняем
        x: newX,
        y: newY,
      });
    };

    const handlePointerUp = (event) => {
      if (isPanning.current) {
        isPanning.current = false;
        boardCanvas.style.cursor = isSpacePressed.current ? 'grab' : ''; // Восстанавливаем курсор grab, если зажат пробел
        try {
          // Проверяем, что захват еще существует, перед тем как отпускать
          if (boardCanvas.hasPointerCapture(event.pointerId)) {
            boardCanvas.releasePointerCapture(event.pointerId);
          }
        } catch (e) { console.warn('Failed to release pointer capture'); }
      }
    };

    // --- Логика для пробела (KeyDown, KeyUp, ContextMenu) ---
    // Остается без изменений
    const handleKeyDown = (event) => {
        if (event.key === ' ' || event.code === 'Space') { // Добавим проверку по code для надежности
             if (isSpacePressed.current) return; // Не реагировать на повторные нажатия
            isSpacePressed.current = true;
            // Предотвращаем скролл страницы пробелом, только если фокус на канвасе или body
            if (document.activeElement === boardCanvas || document.activeElement === document.body) {
               event.preventDefault();
            }
            // Меняем курсор, если мышь над канвасом и не идет пан
            if (boardCanvas.matches(':hover') && !isPanning.current) {
                boardCanvas.style.cursor = 'grab';
            }
        }
    };

    const handleKeyUp = (event) => {
        if (event.key === ' ' || event.code === 'Space') {
            if (!isSpacePressed.current) return; // Избегаем срабатывания, если уже отпустили
            isSpacePressed.current = false;
             // Возвращаем курсор по умолчанию, если не идет пан
             if (!isPanning.current && boardCanvas.style.cursor === 'grab') {
                 boardCanvas.style.cursor = '';
             }
        }
    };

    const handleContextMenu = (event) => {
      // Отключаем стандартное контекстное меню на канвасе
      if (event.target === boardCanvas) {
          event.preventDefault();
      }
    };

    // --- Обновление курсора при наведении/уходе мыши ---
    // Остается без изменений
    const updateCursorOnHover = () => {
        if (isPanning.current) {
            boardCanvas.style.cursor = 'grabbing';
        } else if (isSpacePressed.current) {
            boardCanvas.style.cursor = 'grab';
        } else {
            boardCanvas.style.cursor = ''; // Курсор по умолчанию
        }
    };

    boardCanvas.addEventListener('mouseenter', updateCursorOnHover);
    boardCanvas.addEventListener('mouseleave', () => {
        // Возвращаем курсор по умолчанию при уходе, только если не идет пан
        if (!isPanning.current) {
            boardCanvas.style.cursor = '';
        }
    });
    // Инициализируем курсор, если мышь уже над элементом при загрузке
    if (boardCanvas.matches(':hover')) {
        updateCursorOnHover();
    }

    // --- Добавление слушателей ---
    document.addEventListener('wheel', handleWheel, { passive: false });
    boardCanvas.addEventListener('pointerdown', handlePointerDown);
    // Слушаем pointermove и pointerup на документе, чтобы не терять события, если курсор ушел с канваса
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    // Слушаем keydown и keyup на документе для пробела
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    boardCanvas.addEventListener('contextmenu', handleContextMenu);

    // --- Очистка слушателей ---
    return () => {
      document.removeEventListener('wheel', handleWheel);
      boardCanvas.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      boardCanvas.removeEventListener('contextmenu', handleContextMenu);
      boardCanvas.removeEventListener('mouseenter', updateCursorOnHover);
      boardCanvas.removeEventListener('mouseleave', () => { if (boardCanvas) boardCanvas.style.cursor = ''; }); // Убрал проверку isPanning

      // Сброс состояний при очистке
      isPanning.current = false;
      isSpacePressed.current = false;
      if (boardCanvas) {
          boardCanvas.style.cursor = ''; // Сброс курсора
      }
    };
    // Зависимости: флаг инициализации, layout контейнера и ref на канвас
  }, [state.isInitialized, boardContainerLayout, boardCanvasRef]);
}

export default usePcZoom;
